/*
 * Onboarding 002 — the funnel as data.
 *
 * Every screen is a config object. Roughly 30 of the 54 are "a question and a column of
 * answer cards", identical apart from copy and which store field they write, so they are
 * data here rather than 30 near-identical render functions.
 *
 * COPY is editable. VALUES ARE NOT. Each option is { v: enumValue, l: label }: `l` is the
 * text on screen, `v` is the Prisma enum the API validates against. Changing `v` to
 * anything not in the server's enum makes the whole submit fail validation at the end of
 * the funnel, after the user has answered fifty questions — with nothing in the logs
 * pointing back to this file.
 *
 * `get`/`set` bind a screen to one store field. `skip` hides a screen conditionally.
 *
 * Loaded after quiz-002-core.js, before quiz-002.js.
 */
window.ReformlyQuiz = window.ReformlyQuiz || {};

(function (NS) {
  'use strict';

  var S = function () { return NS.store.state; };
  var set = function (section, patch) { NS.store.set(section, patch); };

  /* Photo briefs from the design files. Real photography replaces these — the shoot list
     is literally what renders, so nothing ships looking finished when it is not. */
  var PHOTO = {
    social: 'Photo: board in a living room, partly folded, morning light',
    reassure: 'Photo: app screen showing a guided session, held in hand',
    personalised: 'Photo: woman mid-session on the board, side angle, natural light',
    time: 'Photo: board unfolded on a kitchen floor, early evening',
    payoff: 'Photo: close crop of core work on the board, natural light',
    sitting: 'Photo: woman doing a back extension on the board, side angle, natural light',
    product: 'Photo: board unfolded, then a second frame folded against a wall, cream background',
    energy: 'Photo: woman stretching by a window, mid afternoon light',
    recipes: 'Photo: app recipe screen held in hand above a kitchen counter',
    sleep: 'Photo: woman asleep, soft morning light, cream bedding',
    breakfast: 'Photo: breakfast plate, overhead, cream linen',
    lunch: 'Photo: lunch bowl, overhead, cream linen',
    dinner: 'Photo: dinner plate, overhead, cream linen',
    letter: 'Photo: the Reformly Letter open on a phone beside a coffee',
  };

  /* W06 and W14 swap one word inside copy the design already wrote. The W14 headline is
     left verbatim — "a flatter, stronger arms" is not a sentence, and writing six
     alternatives would be inventing product copy. */
  var AGE_PHRASE = { '18-29': 'in their 20s', '30-39': 'in their 30s', '40-49': 'in their 40s', '50+': 'over 50' };
  var FOCUS_NOUN = { belly: 'belly', arms: 'arms', legs: 'legs', butt: 'glutes', back: 'back', chest: 'chest' };

  function single(o) { o.kind = 'single'; return o; }
  function multi(o) { o.kind = 'multi'; return o; }
  function grid(o) { o.kind = 'grid'; return o; }
  function images(o) { o.kind = 'images'; return o; }
  function grouped(o) { o.kind = 'grouped'; return o; }
  function inter(o) { o.kind = 'interstitial'; return o; }
  function custom(o) { o.kind = 'custom'; return o; }

  /*
   * Referral code off the URL. Both names are correct: the public
   * /referrals/referrer endpoint resolves friend codes (referralCode) AND affiliate
   * codes (ref) — see referrals.service.ts:25.
   *
   * Read synchronously so STEPS can be built without waiting on the network. The
   * landing step only exists when a code is present, which is what makes a code-less
   * visitor drop straight into the quiz with no flash of a landing screen — the
   * step machine cannot skip index 0 (its skip() loop starts at i > 0).
   */
  NS.referralCode = function () {
    try {
      var q = new URLSearchParams(location.search);
      return q.get('referralCode') || q.get('ref') || null;
    } catch (e) {
      return null;
    }
  };

  var referralSteps = NS.referralCode()
    ? [custom({ name: 'referral_landing', screen: 'referralLanding' })]
    : [];

  /* The prefetch lives in quiz-002-api.js, not here. A microtask scheduled from
     this file drains BEFORE the next deferred script runs, so NS.api does not
     exist yet and the lookup silently resolved null. */
  NS.wantsReferralLanding = referralSteps.length > 0;

  NS.STEPS = referralSteps.concat([
    /* ─── Part 1, about you and your body ────────────────────────────────── */

    custom({ name: 'age_gate', screen: 'ageGate', answer: function () { return S().aboutYou.ageGroup; } }),

    inter({
      name: 'social_proof',
      title: 'Over 50,000 women are already following a Reformly plan',
      body: 'Twenty minutes a day, on a board that folds away when you are done.',
      // Ratios are the files' own: body-shapes is 1200x898, photo-08 1200x1068.
      img: { m: 'social_proof_mobile', w: 'social_proof_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
    }),

    single({
      name: 'pilates_experience', part: 'about', label: 'About you',
      title: 'Have you done Pilates before?',
      options: [
        { v: 'classes_taken', l: 'Yes, I have taken classes' },
        { v: 'a_little_online', l: 'A little, mostly online' },
        { v: 'first_time', l: 'No, this would be my first time' },
      ],
      get: function () { return S().aboutYou.pilatesExperience; },
      set: function (v) { set('aboutYou', { pilatesExperience: v }); },
    }),

    inter({
      name: 'reassurance',
      title: 'You are in the right place',
      body: 'Your plan starts at your level and builds from there. Every session is guided on video, so you always know what to do next.',
      // board-living-room-tv is 1200x898, photo-09 is 1200x1068.
      img: { m: 'reassurance_mobile', w: 'reassurance_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
      footnote: 'The Reformly Pilates Board is sold separately from the app membership.',
      bodyInk: true,
    }),

    single({
      name: 'main_goal', part: 'about', label: 'About you',
      title: 'What is your main goal?',
      options: [
        { v: 'LOSE_WEIGHT', l: 'Lose weight' },
        { v: 'BUILD_MUSCLE', l: 'Build strength and tone up' },
        { v: 'KEEP_FIT', l: 'Stay fit and consistent' },
        { v: 'FIND_SELF_LOVE', l: 'Feel better in my own body' },
      ],
      get: function () { return S().aboutYou.mainGoal; },
      set: function (v) { set('aboutYou', { mainGoal: v }); },
    }),

    inter({
      name: 'personalised',
      title: 'Tone up at home with Reformly',
      body: function () {
        var phrase = AGE_PHRASE[S().aboutYou.ageGroup] || 'in their 30s';
        return 'For women ' + phrase + ', twenty minutes of Pilates a day is enough to feel a change in how your clothes fit.';
      },
      // app-workouts is 1200x898, photo-10 is 1200x1068.
      img: { m: 'personalised_mobile', w: 'personalised_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
      bodyInk: true,
    }),

    multi({
      name: 'secondary_goals', part: 'about', label: 'About you',
      title: 'What else do you want from your plan?',
      variant: 'icon', contentGap: 20,
      options: [
        { v: 'feel_healthier', l: 'Feel healthier', icon: 'health' },
        { v: 'feel_confident', l: 'Feel confident again', icon: 'confidence' },
        { v: 'fit_in_clothes', l: 'Fit back into my clothes', icon: 'fit' },
        { v: 'more_energy', l: 'Have more energy in the day', icon: 'energy' },
        { v: 'improve_posture', l: 'Improve my posture', icon: 'posture' },
        { v: 'feel_calmer', l: 'Feel calmer and less stressed', icon: 'calm' },
      ],
      get: function () { return S().plan.secondaryGoals; },
      set: function (v) { set('plan', { secondaryGoals: v }); },
    }),

    images({
      name: 'body_type', part: 'about', label: 'Your body',
      title: 'Which shape is closest to yours?',
      options: [
        { v: 'RECTANGLE', l: 'Rectangle', img: 'body_rectangle' },
        { v: 'HOURGLASS', l: 'Hourglass', img: 'body_hourglass' },
        { v: 'PEAR', l: 'Pear', img: 'body_pear' },
        { v: 'ROUND', l: 'Round', img: 'body_round' },
      ],
      get: function () { return S().aboutYou.bodyType; },
      set: function (v) { set('aboutYou', { bodyType: v }); },
    }),

    images({
      name: 'dream_body', part: 'about', label: 'Your body',
      title: 'What are you working towards?',
      options: [
        { v: 'THIN', l: 'Thin', img: 'dream_thin' },
        { v: 'TONED', l: 'Toned', img: 'dream_toned' },
        { v: 'CURVY', l: 'Curvy', img: 'dream_curvy' },
        { v: 'FEW_SIZES_SMALLER', l: 'Few sizes smaller', img: 'dream_few_sizes_smaller' },
      ],
      get: function () { return S().aboutYou.dreamBodyShape; },
      set: function (v) { set('aboutYou', { dreamBodyShape: v }); },
    }),

    single({
      name: 'weight_pattern', part: 'about', label: 'Your body',
      title: 'How does your weight usually behave?',
      options: [
        { v: 'hard_to_lose', l: 'It is hard for me to lose' },
        { v: 'shifts_easily', l: 'It shifts easily either way' },
        { v: 'hard_to_gain', l: 'It is hard for me to gain' },
      ],
      get: function () { return S().aboutYou.weightPattern; },
      set: function (v) { set('aboutYou', { weightPattern: v }); },
    }),

    inter({
      name: 'time',
      title: 'Twenty minutes is all the plan asks',
      body: 'Short guided sessions you can do before work or while dinner is in the oven. No studio, no class times, no commute.',
      // board-lunge is 1200x898, photo-11 is 1200x1068.
      img: { m: 'time_mobile', w: 'time_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
      bodyInk: true,
    }),

    single({
      name: 'how_long', part: 'about', label: 'Your body',
      title: 'How long have you felt out of your comfort zone in your body?',
      options: [
        { v: 'LESS_THAN_A_YEAR', l: 'Less than a year' },
        { v: 'ONE_TO_TWO_YEARS', l: 'One to two years' },
        { v: 'MORE_THAN_THREE_YEARS', l: 'Three years or more' },
        { v: 'NEVER', l: 'I have never really felt settled in it' },
      ],
      get: function () { return S().aboutYou.idealWeightPeriod; },
      set: function (v) { set('aboutYou', { idealWeightPeriod: v }); },
    }),

    multi({
      name: 'focus_zones', part: 'about', label: 'Your focus',
      title: 'Where do you want to focus first?',
      variant: 'compact', contentGap: 16, contentGapMd: 32, aside: 'coach',
      options: [
        { v: 'belly', l: 'Belly' }, { v: 'arms', l: 'Arms' }, { v: 'legs', l: 'Legs' },
        { v: 'butt', l: 'Glutes' }, { v: 'back', l: 'Back' }, { v: 'chest', l: 'Chest' },
      ],
      get: function () { return S().aboutYou.focusAreas; },
      set: function (v) { set('aboutYou', { focusAreas: v }); },
    }),

    inter({
      name: 'focus_payoff',
      title: 'A flatter, stronger middle starts here',
      body: function () {
        var zone = FOCUS_NOUN[(S().aboutYou.focusAreas || [])[0]] || 'belly';
        return 'Your plan will put your ' + zone + ' first. Every move is guided on video, so you are never guessing whether you are doing it right.';
      },
      // board-chest-press is 1200x898, photo-12 is 1200x1068.
      img: { m: 'focus_payoff_mobile', w: 'focus_payoff_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
      footnote: 'Every plan comes with our 100-day money-back guarantee.',
      bodyInk: true,
    }),

    /* ─── Part 2, your routine ───────────────────────────────────────────── */

    single({
      name: 'flexibility', part: 'routine', label: 'Your routine',
      title: 'How flexible are you right now?',
      options: [
        { v: 'pretty_flexible', l: 'Pretty flexible' },
        { v: 'just_getting_started', l: 'I am just getting started' },
        { v: 'not_that_good', l: 'Not very' },
        { v: 'not_sure', l: 'I am not sure' },
      ],
      get: function () { return S().lifestyle.flexibilityLevel; },
      set: function (v) { set('lifestyle', { flexibilityLevel: v }); },
    }),

    single({
      name: 'stairs', part: 'routine', label: 'Your routine',
      title: 'How do you feel after a flight of stairs?',
      options: [
        { v: 'out_of_breath_cant_talk', l: 'So out of breath I cannot talk' },
        { v: 'slightly_can_still_talk', l: 'A little, but I can still talk' },
        { v: 'ok_after_one_flight', l: 'Fine after one flight' },
        { v: 'can_handle_few_flights', l: 'I can do a few flights easily' },
      ],
      get: function () { return S().lifestyle.stairsBreathLevel; },
      set: function (v) { set('lifestyle', { stairsBreathLevel: v }); },
    }),

    multi({
      name: 'work_around', part: 'routine', label: 'Your routine',
      title: 'Is there anything we should work around?',
      exclusive: 'no_struggles',
      options: [
        { v: 'sensitive_back', l: 'A sensitive back' },
        { v: 'sensitive_knees', l: 'Sensitive knees' },
        { v: 'sensitive_wrists', l: 'Sensitive wrists' },
        { v: 'postpartum', l: 'I am postpartum' },
        { v: 'no_struggles', l: 'Nothing right now' },
      ],
      get: function () { return S().aboutYou.physicalStruggles; },
      set: function (v) { set('aboutYou', { physicalStruggles: v }); },
    }),

    single({
      name: 'posture', part: 'routine', label: 'Your routine',
      title: 'How would you describe your posture?',
      options: [
        { v: 'upright_and_strong', l: 'Upright and strong' },
        { v: 'mostly_good_sometimes_slouch', l: 'Mostly good, I slouch sometimes' },
        { v: 'often_slouched', l: 'Often slouched' },
        { v: 'constantly_slouching', l: 'Slouched most of the day' },
      ],
      get: function () { return S().lifestyle.postureLevel; },
      set: function (v) { set('lifestyle', { postureLevel: v }); },
    }),

    inter({
      name: 'sitting',
      title: 'Sitting all day is doing the damage, not you',
      body: 'Pilates works the deep muscles that hold you upright. Your plan will build those first, so standing tall stops taking effort.',
      // board-plank is 1200x898, photo-13 is 1200x1068.
      img: { m: 'sitting_mobile', w: 'sitting_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
    }),

    single({
      name: 'workout_often', part: 'routine', label: 'Your routine',
      title: 'How often do you work out at the moment?',
      options: [
        { v: 'five_to_seven', l: 'Almost every day' },
        { v: 'three_to_five', l: 'A few times a week' },
        { v: 'few_times_a_month', l: 'A few times a month' },
        { v: 'not_much', l: 'Barely at all' },
      ],
      get: function () { return S().lifestyle.workoutFrequency; },
      set: function (v) { set('lifestyle', { workoutFrequency: v }); },
    }),

    single({
      name: 'walks', part: 'routine', label: 'Your routine',
      title: 'And how often do you go for a walk?',
      options: [
        { v: 'almost_every_day', l: 'Almost every day' },
        { v: 'three_to_four_times_week', l: 'Three or four times a week' },
        { v: 'one_to_two_times_week', l: 'Once or twice a week' },
        { v: 'once_a_month', l: 'Once a month or so' },
      ],
      get: function () { return S().lifestyle.walkFrequency; },
      set: function (v) { set('lifestyle', { walkFrequency: v }); },
    }),

    single({
      name: 'reformer', part: 'routine', label: 'Your routine',
      title: 'Have you tried a reformer before?',
      options: [
        { v: 'yes_loved_it', l: 'Yes, and I loved it', sub: 'Studio or at home' },
        { v: 'yes_didnt_work', l: 'Yes, but it did not stick' },
        { v: 'no_never_tried', l: 'No, never' },
      ],
      get: function () { return S().aboutYou.reformerExperience; },
      set: function (v) { set('aboutYou', { reformerExperience: v }); },
    }),

    multi({
      name: 'held_back', part: 'routine', label: 'Your routine',
      title: 'What has held you back from trying one?',
      // Only meaningful for someone who has never tried a reformer.
      skip: function () { return S().aboutYou.reformerExperience !== 'no_never_tried'; },
      exclusive: 'none',
      options: [
        { v: 'appears_challenging', l: 'It looks difficult' },
        { v: 'seems_pricey', l: 'Studio classes are expensive' },
        { v: 'uncertain_effectiveness', l: 'I am not sure it would work for me' },
        { v: 'worried_proper_usage', l: 'I would not know if I was doing it right' },
        { v: 'prefer_bodyweight', l: 'I prefer bodyweight workouts' },
        { v: 'no_space', l: 'I have nowhere to store one' },
        { v: 'none', l: 'Nothing has held me back' },
      ],
      get: function () { return S().aboutYou.reformerSkipReasons; },
      set: function (v) { set('aboutYou', { reformerSkipReasons: v }); },
    }),

    inter({
      name: 'product_reveal',
      title: 'The reformer, made to live in your home',
      body: 'The Reformly Pilates Board gives you the resistance of a studio reformer, then folds flat and slides under the sofa. Every workout on it is guided in the app.',
      // board-folded-sofa is 1200x898, photo-14 is 1200x1068.
      img: { m: 'product_reveal_mobile', w: 'product_reveal_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
      footnote: 'The board is sold separately from the app membership.',
    }),

    /* ─── Part 3, your day ───────────────────────────────────────────────── */

    single({
      name: 'work_week', part: 'day', label: 'Your day',
      title: 'What does your work week look like?',
      options: [
        { v: 'nine_to_five', l: 'Nine to five' },
        { v: 'shift_work', l: 'Shift work' },
        { v: 'flexible_hours', l: 'My hours are flexible' },
        { v: 'retired_not_working', l: 'I am retired or not working right now' },
      ],
      get: function () { return S().lifestyle.workSchedule; },
      set: function (v) { set('lifestyle', { workSchedule: v }); },
    }),

    single({
      name: 'typical_day', part: 'day', label: 'Your day',
      title: 'How would you describe a typical day?',
      options: [
        { v: 'mostly_seated', l: 'I sit for most of it' },
        { v: 'moderately_active', l: 'I move around a fair bit' },
        { v: 'on_feet_all_day', l: 'I am on my feet all day' },
      ],
      get: function () { return S().lifestyle.typicalDay; },
      set: function (v) { set('lifestyle', { typicalDay: v }); },
    }),

    single({
      name: 'sitting_hours', part: 'day', label: 'Your day',
      title: 'How many hours a day do you spend sitting?',
      options: [
        { v: 'two_to_three_hours', l: 'Two to three' },
        { v: 'four_to_six_hours', l: 'Four to six' },
        { v: 'seven_to_eight_hours', l: 'Seven to eight' },
        { v: 'eight_plus_hours', l: 'More than eight' },
      ],
      get: function () { return S().lifestyle.dailySittingTime; },
      set: function (v) { set('lifestyle', { dailySittingTime: v }); },
    }),

    grid({
      name: 'sleep', part: 'day', label: 'Your day',
      title: 'How much sleep do you usually get?',
      // Frame ratios from the design (350x200 mobile, 560x220 web), not the files' —
      // woman-asleep is 1.34 and photo-05 is 2.85, so both crop to fill.
      img: { m: 'sleep_mobile', w: 'sleep_web', ar: '350 / 200', arWeb: '560 / 220' },
      options: [
        { v: 'less_than_five', l: 'Less than five hours' },
        { v: 'five_to_six', l: 'Five to six hours' },
        { v: 'seven_to_eight', l: 'Seven to eight hours' },
        { v: 'more_than_eight', l: 'More than eight hours' },
      ],
      get: function () { return S().lifestyle.sleepDuration; },
      set: function (v) { set('lifestyle', { sleepDuration: v }); },
    }),

    single({
      name: 'energy', part: 'day', label: 'Your day',
      title: 'How is your energy through the day?',
      options: [
        { v: 'low_from_wake_up', l: 'Low from the moment I wake up' },
        { v: 'dip_around_lunch', l: 'It dips after lunch' },
        { v: 'dip_before_meals', l: 'It dips before I eat' },
        { v: 'even_throughout', l: 'Steady and good' },
      ],
      get: function () { return S().lifestyle.energyLevel; },
      set: function (v) { set('lifestyle', { energyLevel: v }); },
    }),

    inter({
      name: 'energy_back',
      title: 'The right kind of movement gives energy back',
      body: 'Twenty minutes of Pilates is not the kind of workout that flattens you for the rest of the day. Most women say the afternoon dip softens within two weeks.',
      // two-women-plank is 1200x898, photo-15 is 1200x1068 — both match the interstitial
      // frames, so the file ratios are the frame ratios here.
      img: { m: 'energy_back_mobile', w: 'energy_back_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
    }),

    single({
      name: 'water', part: 'day', label: 'Your day',
      title: 'How much water do you drink in a day?',
      options: [
        { v: 'coffee_or_tea_only', l: 'Mostly coffee or tea' },
        { v: 'about_two_glasses', l: 'About two glasses' },
        { v: 'two_to_six_glasses', l: 'Two to six glasses' },
        { v: 'more_than_six_glasses', l: 'More than six glasses' },
      ],
      get: function () { return S().lifestyle.dailyWaterIntake; },
      set: function (v) { set('lifestyle', { dailyWaterIntake: v }); },
    }),

    /* ─── Part 4, your food ──────────────────────────────────────────────── */

    grid({
      name: 'breakfast', part: 'food', label: 'Your food',
      title: 'When do you usually have breakfast?',
      // Frame is the design's 350x180 / 560x200, not the files' 1.63 and 2.64 — both crop.
      img: { m: 'breakfast_mobile', w: 'breakfast_web', ar: '350 / 180', arWeb: '560 / 200' },
      options: [
        { v: 'six_to_eight_am', l: 'Between six and eight' },
        { v: 'eight_to_ten_am', l: 'Between eight and ten' },
        { v: 'ten_am_to_noon', l: 'Between ten and noon' },
        { v: 'skip_breakfast', l: 'I usually skip it' },
      ],
      get: function () { return S().nutrition.breakfastTime; },
      set: function (v) { set('nutrition', { breakfastTime: v }); },
    }),

    grid({
      name: 'lunch', part: 'food', label: 'Your food',
      title: 'And lunch?',
      // Frame is the design's 350x180 / 560x200, not the files' 1.63 and 2.64 — both crop.
      img: { m: 'lunch_mobile', w: 'lunch_web', ar: '350 / 180', arWeb: '560 / 200' },
      options: [
        { v: 'ten_am_to_noon', l: 'Between ten and noon' },
        { v: 'noon_to_two_pm', l: 'Between noon and two' },
        { v: 'two_to_four_pm', l: 'Between two and four' },
        { v: 'skip_lunch', l: 'I usually skip it' },
      ],
      get: function () { return S().nutrition.lunchTime; },
      set: function (v) { set('nutrition', { lunchTime: v }); },
    }),

    grid({
      name: 'dinner', part: 'food', label: 'Your food',
      title: 'And dinner?',
      // Frame is the design's 350x180 / 560x200, not the files' 1.63 and 2.64 — both crop.
      img: { m: 'dinner_mobile', w: 'dinner_web', ar: '350 / 180', arWeb: '560 / 200' },
      options: [
        { v: 'four_to_six_pm', l: 'Between four and six' },
        { v: 'six_to_eight_pm', l: 'Between six and eight' },
        { v: 'eight_to_ten_pm', l: 'Between eight and ten' },
        { v: 'skip_dinner', l: 'I usually skip it' },
      ],
      get: function () { return S().nutrition.dinnerTime; },
      set: function (v) { set('nutrition', { dinnerTime: v }); },
    }),

    grouped({
      name: 'food_preference', part: 'food', label: 'Your food',
      title: 'What kind of food do you prefer?',
      groups: [
        {
          heading: 'With meat',
          options: [
            { v: 'traditional', l: 'Everything', sub: 'I am not fussy' },
            // LOW_CARB and WHOLE_FOODS are their own enum values, NOT keto/paleo. Keto is
            // <50g carbs inducing ketosis; paleo excludes grains, legumes and dairy.
            // Reusing those would mislabel most of this bucket, and dietType is what the
            // recipe filter will key on.
            { v: 'low_carb', l: 'Low carb', sub: 'I keep carbs down' },
            { v: 'whole_foods', l: 'Whole foods', sub: 'I avoid anything processed' },
          ],
        },
        {
          heading: 'Without meat',
          options: [
            { v: 'vegetarian', l: 'Vegetarian', sub: 'No meat or fish' },
            { v: 'vegan', l: 'Vegan', sub: 'No animal products at all' },
          ],
        },
        {
          heading: 'With fish',
          options: [{ v: 'pescatarian', l: 'Pescatarian', sub: 'Fish but no meat' }],
        },
      ],
      get: function () { return S().nutrition.dietType; },
      set: function (v) { set('nutrition', { dietType: v }); },
    }),

    multi({
      name: 'food_habits', part: 'food', label: 'Your food',
      title: 'Do any of these sound like you?',
      exclusive: 'none',
      options: [
        { v: 'late_night_eating', l: 'I eat late at night' },
        { v: 'sweet_tooth', l: 'I have a sweet tooth' },
        { v: 'stress_snacking', l: 'I snack when I am stressed' },
        { v: 'drinks_soda', l: 'I drink a lot of soft drinks' },
        { v: 'salty_foods', l: 'I eat a lot of salty food' },
        { v: 'none', l: 'None of these' },
      ],
      get: function () { return S().lifestyle.foodHabits; },
      set: function (v) { set('lifestyle', { foodHabits: v }); },
    }),

    inter({
      name: 'recipes',
      title: 'Meals that fit around the plan, not the other way round',
      body: 'Your membership includes sixty recipes built for this, with a shopping list that fills itself. Nothing is off limits and nothing gets weighed.',
      // photo-03 is 1200x898, photo-16 is 1200x1068 — both match the interstitial frames.
      img: { m: 'recipes_mobile', w: 'recipes_web', ar: '1200 / 898', arWeb: '1200 / 1068' },
    }),

    /* ─── Part 5, almost there ───────────────────────────────────────────── */

    multi({
      name: 'setbacks', part: 'almost', label: 'Almost there',
      title: 'Has anything made it harder to stay on track these last few years?',
      exclusive: 'none',
      options: [
        { v: 'marriage_or_relationship', l: 'A new relationship' },
        { v: 'busy_work_or_family', l: 'A busy job or family life' },
        { v: 'having_a_baby', l: 'Having a baby' },
        { v: 'financial_struggles', l: 'Money worries' },
        { v: 'injury_or_health_issue', l: 'An injury or health issue' },
        { v: 'stress_or_worry', l: 'Stress or worry' },
        { v: 'none', l: 'None of these' },
      ],
      get: function () { return S().lifestyle.weightGainTriggers; },
      set: function (v) { set('lifestyle', { weightGainTriggers: v }); },
    }),

    custom({ name: 'height', part: 'almost', label: 'Almost there', screen: 'height',
      blocked: function () { return !S().auth.healthDataConsent; },
      answer: function () { return S().metrics.height.value; } }),
    custom({ name: 'weight', part: 'almost', label: 'Almost there', screen: 'weight',
      answer: function () { return S().metrics.currentWeight.value; } }),
    custom({ name: 'target', part: 'almost', label: 'Almost there', screen: 'target',
      answer: function () { return S().metrics.goalWeight.value; } }),
    custom({ name: 'age', part: 'almost', label: 'Almost there', screen: 'dob',
      // The band, not the date — a DOB is personal data and does not belong in analytics.
      answer: function () { return S().aboutYou.ageGroup; } }),
    custom({ name: 'profile', screen: 'profile' }),

    single({
      name: 'event', part: 'almost', label: 'Almost there',
      title: 'Is there anything coming up you want to feel good for?',
      subtitle: 'Something to aim at makes a plan easier to keep',
      options: [
        { v: 'holiday', l: 'A holiday' },
        { v: 'wedding', l: 'A wedding' },
        { v: 'birthday', l: 'A birthday' },
        { v: 'trip', l: 'A trip' },
        { v: 'reunion', l: 'A reunion' },
        { v: 'photo_moment', l: 'A photo I will keep' },
        { v: 'no_special_event', l: 'Nothing in particular' },
      ],
      get: function () { return S().plan.specialEvent; },
      set: function (v) { set('plan', { specialEvent: v }); },
    }),

    custom({
      name: 'event_date', part: 'almost', label: 'Almost there', screen: 'eventDate',
      skip: function () {
        var e = S().plan.specialEvent;
        return !e || e === 'no_special_event';
      },
      answer: function () { return S().plan.specialEventDate ? 'set' : 'skipped'; },
    }),

    custom({ name: 'outlook', screen: 'outlook' }),
    custom({ name: 'email', screen: 'email' }),
    custom({ name: 'opt_in', screen: 'optIn',
      answer: function () { return String(S().lifestyle.marketingEmailsOptIn); } }),
    custom({ name: 'display_name', screen: 'displayName' }),
    custom({ name: 'username', screen: 'username' }),
    custom({ name: 'country', screen: 'country',
      answer: function () { return S().auth.quizCountry; } }),

    /* D10 sits last of the data screens — it is the single submit point. */
    custom({ name: 'building_plan', screen: 'building' }),
    custom({ name: 'plan_ready', screen: 'planReady' }),
    custom({ name: 'scratch_card', screen: 'scratch' }),
  ]);

  /* The progress bar shows five segments — one per part — with the finer section label
     above. The design files show seven labels against five segments and fills that
     contradict each other, so the parts are the rule. */
  NS.PARTS = ['about', 'routine', 'day', 'food', 'almost'];
})(window.ReformlyQuiz);
